import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { API_URL } from '../../utils/config';
import { COLORS, FONTS } from '../../theme/tokens';

export const CATEGORY_BAND_PADDING = {
  xs: '32px 16px',
  md: '56px 24px',
};

const HomepageVisuals = () => {
  const { t, i18n } = useTranslation();
  // Category/attribute titles are resolved server-side, so the fetch is keyed
  // by the active language rather than parsed in the component.
  const activeLang = (i18n.language || 'EN').toUpperCase().slice(0, 2);
  const [visuals, setVisuals] = useState({ categories: [], attributes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHomepageVisuals = async () => {
      try {
        setLoading(true);
        setError(null);
        const url = `${API_URL}/products/homepage?lang=${encodeURIComponent(activeLang)}`;
        const response = await fetch(url);
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch homepage visuals');
        }
        const data = await response.json();
        setVisuals({
          categories: data.categories || [],
          attributes: data.attributes || [],
        });
      } catch (fetchError) {
        setError(fetchError.message);
        console.error('Error fetching homepage visuals:', fetchError);
      } finally {
        setLoading(false);
      }
    };

    fetchHomepageVisuals();
  }, [activeLang]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, color: 'error.main', textAlign: 'center' }}>
        Error loading homepage content: {error}
      </Box>
    );
  }

  const hasCategories = visuals.categories.length > 0;
  const hasAttributes = visuals.attributes.length > 0;

  if (!hasCategories && !hasAttributes) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        {t('shop.noCategories')}
      </Box>
    );
  }

  const renderItems = (items, type) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        gap: { xs: '12px', md: '20px' },
        maxWidth: 1232,
        mx: 'auto',
      }}
    >
      {items.map((item) => {
        const href = type === 'category'
          ? `/category/${item.slug}`
          : `/product-category/${item.id}`;

        return (
          <Box key={`${type}-${item.id}`}>
            <Box
              component={RouterLink}
              to={href}
              sx={{
                display: 'block',
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <Box
                component="img"
                src={item.image}
                alt={item.title}
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  aspectRatio: '4/5',
                  borderRadius: '2px',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            </Box>
            <Typography
              component="p"
              align="center"
              sx={{
                fontFamily: FONTS.heading,
                fontWeight: 600,
                fontSize: '1.125rem',
                color: COLORS.textPrimary,
                mt: 1.5,
              }}
            >
              {item.title}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box
      component="section"
      sx={{
        bgcolor: COLORS.surfacePanel,
        p: CATEGORY_BAND_PADDING,
      }}
    >
      {hasCategories && (
        <Box>
          <Typography
            component="h2"
            align="center"
            sx={{
              fontFamily: FONTS.heading,
              fontWeight: 600,
              fontSize: '1.875rem',
              color: COLORS.textPrimary,
              mb: 3,
            }}
          >
            {t('home.shopByCategory')}
          </Typography>
          {renderItems(visuals.categories, 'category')}
        </Box>
      )}

      {hasAttributes && (
        <Box sx={{ mt: hasCategories ? { xs: 5, md: 7 } : 0 }}>
          <Typography
            component="h2"
            align="center"
            sx={{
              fontFamily: FONTS.heading,
              fontWeight: 600,
              fontSize: '1.875rem',
              color: COLORS.textPrimary,
              mb: 3,
            }}
          >
            {t('shop.discoverCollections')}
          </Typography>
          {renderItems(visuals.attributes, 'attribute')}
        </Box>
      )}
    </Box>
  );
};

export default HomepageVisuals;
